import type { NonEmptyArray } from '@crossbeam/types';

import { computed } from 'vue';

import { useApiKeysStore, useWebhooksStore } from '@/stores';

import { API_INTEGRATION_FIXTURES } from './fixtures/integrations';
import ApiFiltersTab from './tabs/ApiFiltersTab.vue';
import ApiIntegrationsTab from './tabs/ApiIntegrationsTab.vue';
import ApiKeysTab from './tabs/ApiKeysTab.vue';
import ApiWebhooksTab from './tabs/ApiWebhooksTab.vue';
import type { ApiTabType } from './types';
import useApiKeyCreateGate from './useApiKeyCreateGate';

export default function useApiTabs() {
  const webhooksStore = useWebhooksStore();
  const apiKeysStore = useApiKeysStore();
  const { createDisabledReason: createKeyDisabledReason } =
    useApiKeyCreateGate();

  const tabs = computed<NonEmptyArray<ApiTabType>>(() => [
    {
      value: 'webhooks',
      name: 'Webhooks',
      component: ApiWebhooksTab,
      count: webhooksStore.webhooks.length,
      createCtaText: 'Create webhook',
    },
    {
      value: 'integrations',
      name: 'API Integrations',
      component: ApiIntegrationsTab,
      count: API_INTEGRATION_FIXTURES.length,
      createCtaText: 'Create integration',
    },
    {
      value: 'filters',
      name: 'API Filters',
      component: ApiFiltersTab,
      count: 0,
      createCtaText: 'Create filter',
    },
    {
      value: 'keys',
      name: 'API Keys',
      component: ApiKeysTab,
      count: apiKeysStore.apiKeys.length,
      createCtaText: 'Create API key',
      createDisabledReason: createKeyDisabledReason.value,
    },
  ]);

  function getTabByValue(value: string | undefined) {
    return tabs.value.find((tab) => tab.value === value) ?? tabs.value[0];
  }

  return { tabs, getTabByValue };
}
